/* ===========================================================================
 * CHOOSE DEVICE / CHOOSE COLOR — the page steps back so the field can step up.
 *
 * Pressing the label above the selectors used to scroll the panel into view and
 * swell its edge for 1.7 seconds. That points at a panel; it does not walk
 * anyone through it. This dims and blurs the whole page and cuts a hole over
 * one field at a time, advancing as each choice is made.
 *
 * IT IS A HOLE IN THE SCRIM, NOT A RAISED FIELD, AND THAT IS THE WHOLE DESIGN.
 *
 * The obvious implementation — put the field above the scrim with a z-index —
 * cannot work on this page. On desktop .Product__Info is position:sticky, which
 * makes it a stacking context, so no z-index on any descendant of it can ever
 * beat a scrim that lives in <body>: measured, z-index 99999 on the panel still
 * painted under .PageOverlay. On a handset .Product__Info is static and the
 * same z-index DOES escape — so the z-index approach needs two different
 * behaviours at two widths and breaks the moment either rule changes.
 *
 * clip-path with an even-odd fill rule sidesteps all of it. The scrim keeps its
 * z-index of 10 and simply is not painted over the rectangle we cut, and
 * hit-testing follows the paint: measured with elementsFromPoint, the centre of
 * the hole returns the button with no .PageOverlay in the chain, while a point
 * 80px below returns the scrim. So the field is genuinely clickable and
 * focusable, outside clicks are genuinely caught, and none of it depends on
 * stacking contexts, on the viewport width, or on adding a single element to
 * the DOM.
 *
 * The scrim is the theme's own <div class="PageOverlay">, the same one the
 * drawers and the mega menu use. It is driven here by a class of our own rather
 * than by .is-visible, so the two mechanisms cannot tread on each other.
 * =========================================================================== */
(function () {
  'use strict';
  if (window.__gcVariantSpotlight) return;

  var PAD = 10;                 /* breathing room around the spotlit field */
  var NAME = 'gc-variant-spotlight';

  /* The fill-rule argument to polygon() is the newest part of this and it is
     the one thing with no graceful degradation — without it the polygon is a
     solid block and the scrim would cover the field it is meant to reveal. If
     it is not supported we decline to open at all and the caller falls back to
     the old point-at-the-panel behaviour, which still works everywhere. */
  var CAN_CUT = !!(window.CSS && CSS.supports &&
    (CSS.supports('clip-path', 'polygon(evenodd, 0px 0px, 1px 0px, 1px 1px)') ||
     CSS.supports('-webkit-clip-path', 'polygon(evenodd, 0px 0px, 1px 0px, 1px 1px)')));

  var scrim = null, panel = null, label = null;
  var steps = [], idx = -1, isOpen = false, ticking = 0;

  function stillMedia(q) {
    return !!(window.matchMedia && window.matchMedia(q).matches);
  }
  function reducedMotion() { return stillMedia('(prefers-reduced-motion: reduce)'); }

  function headerHeight() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
    var n = parseInt(raw, 10);
    return isNaN(n) ? 64 : n;
  }

  /* The fields, in the order the shopper meets them. A slider and the swatch
     list inside it would both match, so anything contained by an earlier hit is
     dropped — otherwise the same control would get two turns. */
  function collect() {
    if (!panel) return [];
    var all = [].slice.call(panel.querySelectorAll(
      '.ProductForm__SelectorButton, .ProductForm__ColorSwatchesSlider, .ProductForm__ColorSwatches, .SizeSwatchList'
    ));
    var out = [];
    all.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;                 /* hidden option */
      for (var i = 0; i < out.length; i++) if (out[i].contains(el)) return;
      out.push(el);
    });
    return out;
  }

  /* polygon() has no notion of a corner radius, so a rounded hole has to be
     traced as points. Six segments a corner is smooth at these radii and
     keeps the path short enough to rewrite on every scroll frame.
     Without this the hole is a hard rectangle sitting over fields that are
     themselves rounded, and the mismatch is visible at every corner. */
  function roundedRingPoints(l, t, r, b, rad) {
    var w = r - l, h = b - t;
    rad = Math.max(0, Math.min(rad, Math.min(w, h) / 2));
    var pts = [], SEG = 6;
    function arc(cx, cy, start) {
      for (var i = 0; i <= SEG; i++) {
        var a = start + (Math.PI / 2) * (i / SEG);
        pts.push(Math.round(cx + Math.cos(a) * rad) + 'px ' + Math.round(cy + Math.sin(a) * rad) + 'px');
      }
    }
    arc(r - rad, t + rad, -Math.PI / 2);
    arc(r - rad, b - rad, 0);
    arc(l + rad, b - rad, Math.PI / 2);
    arc(l + rad, t + rad, Math.PI);
    return pts.join(',');
  }

  function cut(el) {
    var r = el.getBoundingClientRect();
    var top = Math.max(headerHeight(), Math.round(r.top - PAD));
    var bottom = Math.min(window.innerHeight, Math.round(r.bottom + PAD));
    var left = Math.max(0, Math.round(r.left - PAD));
    var right = Math.min(window.innerWidth, Math.round(r.right + PAD));

    /* the hole follows the radius of the thing it is revealing */
    var rad = parseFloat(getComputedStyle(el).borderRadius) || 0;
    if (!rad || rad < 8) rad = 16;
    rad += PAD;

    /* The outer ring is the viewport; the inner ring is the hole. evenodd is
       what makes the second ring subtract rather than add. */
    var poly = 'polygon(evenodd,' +
      '0px 0px,100% 0px,100% 100%,0px 100%,0px 0px,' +
      roundedRingPoints(left, top, right, bottom, rad) + ')';
    scrim.style.clipPath = poly;
    scrim.style.webkitClipPath = poly;
  }

  /* The sticky column moves under the page as it scrolls and the hole is in
     viewport coordinates, so the two have to be kept in step. */
  function sync() {
    if (!isOpen || ticking) return;
    ticking = requestAnimationFrame(function () {
      ticking = 0;
      if (isOpen && steps[idx]) cut(steps[idx]);
    });
  }

  function focusInto(el) {
    var target = el;
    if (!el.matches('.ProductForm__SelectorButton')) {
      target = el.querySelector('[aria-pressed="true"], [tabindex], button') || el;
    }
    try { target.focus({ preventScroll: true }); } catch (e) { try { target.focus(); } catch (e2) {} }
  }

  function go(i) {
    if (!isOpen) return;
    if (i >= steps.length) { close(); return; }
    if (steps[idx]) steps[idx].classList.remove('gc-vspot-target');
    idx = i;
    var el = steps[idx];
    el.classList.add('gc-vspot-target');
    el.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
    cut(el);
    /* The smooth scroll is still running, so the first measurement is of a
       field that has not arrived yet. Re-cut as it travels.

       Deliberately a timeout ladder and NOT a requestAnimationFrame loop: rAF
       is throttled to nothing in a background or occluded tab, and a rAF-only
       chase leaves the hole stranded wherever the field happened to be on the
       first frame — measured, it produced a degenerate hole pinned to 0px.
       Timers still fire there. The rAF path in sync() below is for scrolling,
       where smoothness matters and a stale frame costs nothing. */
    [0, 60, 140, 260, 420, 650, 900].forEach(function (t) {
      setTimeout(function () { if (isOpen && idx === i) cut(el); }, t);
    });
    setTimeout(function () { if (isOpen && idx === i) focusInto(el); }, reducedMotion() ? 0 : 320);
  }

  function next() { go(idx + 1); }

  /* --- the listeners that only exist while it is open --- */
  function onKey(e) { if (e.key === 'Escape' || e.key === 'Esc') close(); }
  function onScrimClick(e) { if (e.target === scrim) close(); }
  function onChosen() {
    /* product-form.js debounces this by 200ms, so by the time it lands the
       selection really has changed rather than merely been hovered */
    if (isOpen) next();
  }

  function open() {
    if (isOpen) return true;
    if (!CAN_CUT) return false;
    scrim = document.querySelector('.PageOverlay');
    panel = document.querySelector('.Product__Info .ProductForm__Variants') ||
            document.querySelector('.ProductForm__Variants');
    if (!scrim || !panel) return false;
    /* another overlay already owns the scrim — do not fight it */
    if (scrim.classList.contains('is-visible')) return false;

    steps = collect();
    if (!steps.length) return false;

    isOpen = true;
    idx = -1;
    document.body.classList.add('gc-vspot-on');
    scrim.classList.add('gc-vspot');
    label = document.querySelector('[data-gc-variants-label]');
    if (label) label.setAttribute('aria-expanded', 'true');

    document.addEventListener('keydown', onKey);
    scrim.addEventListener('click', onScrimClick);
    document.addEventListener('variant:changed', onChosen);
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    if (window.GCOverlayBack) window.GCOverlayBack.opened(NAME, close);
    next();
    return true;
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    if (ticking) { cancelAnimationFrame(ticking); ticking = 0; }

    document.removeEventListener('keydown', onKey);
    if (scrim) scrim.removeEventListener('click', onScrimClick);
    document.removeEventListener('variant:changed', onChosen);
    window.removeEventListener('scroll', sync);
    window.removeEventListener('resize', sync);

    steps.forEach(function (el) { el.classList.remove('gc-vspot-target'); });
    steps = []; idx = -1;

    /* A scrim left half-configured breaks every drawer on the page, so every
       property this code can write is cleared here, not just the ones it is
       likely to have written. */
    if (scrim) {
      scrim.classList.remove('gc-vspot');
      scrim.style.clipPath = '';
      scrim.style.webkitClipPath = '';
    }
    document.body.classList.remove('gc-vspot-on');
    if (label) { label.setAttribute('aria-expanded', 'false'); try { label.focus(); } catch (e) {} }
    if (window.GCOverlayBack) window.GCOverlayBack.closed(NAME);
  }

  window.__gcVariantSpotlight = { open: open, close: close, supported: CAN_CUT };
})();
